/**
 * "Edit setup" — the way back to the header fields a plan was created with.
 *
 * `PlanSetupForm` is the CREATE flow (RP-1.1/1.2/1.3): a four-step page with a Solution
 * cascade, a Pod Shape and an estimate import, none of which make sense once a plan
 * already exists. This dialog is a narrower, edit-only surface over the same fields the
 * PATCH endpoint (`updateResourcePlanSchema`) accepts for an existing Draft.
 *
 * Deliberately excluded, because the update schema has no such field or the field has its
 * own dedicated surface: `solutionId` (a plan belongs to its Solution for life),
 * `podShapeId` (owned by the Planned Pod tab), `discountPercent` / `discountAmount` /
 * `discountReason` (their own rules, out of scope here), `kind`, and
 * `engagementStandardTierId`.
 *
 * Two sharp edges this dialog has to handle rather than let the server 422 on:
 *
 * 1. **The entryMode trap.** A SteadyState plan can only be Monthly (§ RP-4.4). Switching
 *    the cadence away from Monthly while the plan is SteadyState also sends
 *    `entryMode: "MonthByMonth"` in the same PATCH — lossless, the server materialises the
 *    run-rate into every month.
 * 2. **Monthly means one figure a month.** A plan created Monthly enters each line as a
 *    single hours-per-month run-rate (SteadyState). Switching a plan TO Monthly used to
 *    change only the label and leave it month by month; it now also sends
 *    `entryMode: "SteadyState"` whenever every line is level across the window, which the
 *    server collapses losslessly. A line whose months differ keeps the plan month by month,
 *    and the dialog says which line.
 * 3. **A fee belongs to its cadence.** A Total fee is the whole engagement; a Monthly fee is
 *    one month. Switching the cadence on a plan that has a fee clears the fee field and asks
 *    for the new one — keeping the number would silently read a $378k total as $378k a
 *    month. Switching back to the plan's own cadence restores the saved fee.
 * 4. **Dates live in "Change dates" (RP-28), not here.** Editing the start month here used
 *    to delete every hour before it rather than move it. The window is now changed only
 *    through `PlanDatesDialog` — Move, or Fit to new dates — which previews before it
 *    writes. This dialog shows the dates read-only, with a button that opens it.
 */

import { zodResolver } from "@hookform/resolvers/zod";
import { AlertTriangle } from "lucide-react";
import { useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { type CreatePlanInput, useUpdateResourcePlan } from "@/hooks/use-resource-plans";
import { formatPlanMonth } from "@/lib/plan-format";
import { monthList, outOfWindowMonths, unevenRunRateLines } from "@/lib/plan-window";
import type { ResourcePlanDetailView } from "@/schemas/resource-plans";

const DECIMAL2 = /^\d+(\.\d{1,2})?$/;

const schema = z.object({
  name: z.string().trim().min(1, { message: "Give the plan a name" }).max(120),
  pricingModel: z.enum(["TimeAndMaterials", "FixedPrice"]),
  budgetCadence: z.enum(["Monthly", "Total"]),
  rateMode: z.enum(["Card", "ClientDictated"]),
  contractedFee: z.string().regex(DECIMAL2).or(z.literal("")).optional(),
  contractedFeeReference: z.string().trim().max(120).optional(),
  contingencyPercent: z
    .string()
    .regex(DECIMAL2)
    .refine((v) => Number(v) <= 100, { message: "must be between 0 and 100" })
    .or(z.literal(""))
    .optional(),
  commercialNotes: z.string().trim().max(4000).optional(),
});
// The server's no-fee rule — no fee on a Monthly T&M plan — is deliberately NOT restated.
// The fee input is disabled in exactly that state, so a client-side rejection would be
// unclearable: the planner would be told to clear a value they cannot reach. `onSubmit`
// resolves it instead by sending a null fee whenever the fee is not allowed, and the
// disabled field's FormDescription already explains why.

type FormValues = z.infer<typeof schema>;

function isPricingModel(v: string): v is FormValues["pricingModel"] {
  return v === "TimeAndMaterials" || v === "FixedPrice";
}
function isBudgetCadence(v: string): v is FormValues["budgetCadence"] {
  return v === "Monthly" || v === "Total";
}
function isRateMode(v: string): v is FormValues["rateMode"] {
  return v === "Card" || v === "ClientDictated";
}

/**
 * The out-of-window notice for a plan's CURRENT (already-saved) window — rendered beside
 * `FeeVarianceLine` on the plan header, not only inside the edit dialog, because narrowing
 * can also have happened in a version this session did not narrow itself.
 *
 * A HISTORICAL condition, not a current policy: since a window narrowed FROM HERE ON
 * discards those hours on save (see the "Save and discard" button below), the only plans
 * that can still show this are ones narrowed before that shipped. It explains why such a
 * plan's totals exceed its visible columns, and warns that touching the window again will
 * finish the job — deleting them for good.
 *
 * Month-by-month plans only: a SteadyState plan has no stored cells to strand.
 */
export function PlanOutOfWindowNotice({ plan }: { plan: ResourcePlanDetailView }) {
  const months =
    plan.entryMode === "MonthByMonth" ? outOfWindowMonths(plan.lines, new Set(plan.months)) : [];
  if (months.length === 0) return null;
  return (
    <p className="flex items-start gap-2 rounded-md bg-warning-100 p-2.5 text-sm text-warning-700 dark:text-warning-400">
      <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
      <span>
        <span className="font-semibold">
          {months.length} month{months.length === 1 ? "" : "s"} of hours entered before the window
          was last narrowed {months.length === 1 ? "sits" : "sit"} outside it
        </span>{" "}
        ({monthList(months)}). They still count toward the plan&rsquo;s totals for now &mdash;
        narrowing the window again will permanently discard them.
      </span>
    </p>
  );
}

/** The form's starting state for a plan, as the dialog opens. */
function prefillValues(plan: ResourcePlanDetailView): FormValues {
  return {
    name: plan.name,
    pricingModel: isPricingModel(plan.pricingModel) ? plan.pricingModel : "TimeAndMaterials",
    budgetCadence: isBudgetCadence(plan.budgetCadence) ? plan.budgetCadence : "Monthly",
    rateMode: isRateMode(plan.rateMode) ? plan.rateMode : "Card",
    contractedFee: plan.contractedFee ?? "",
    contractedFeeReference: plan.contractedFeeReference ?? "",
    contingencyPercent: plan.contingencyPercent ?? "",
    commercialNotes: plan.commercialNotes ?? "",
  };
}

/** True when two optional decimal strings mean the same number — `"1000"` is not a change to `"1000.00"`. */
function sameDecimal(a: string | null, b: string | null): boolean {
  if (a === null || b === null) return a === b;
  return Number(a) === Number(b);
}

interface Props {
  plan: ResourcePlanDetailView;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Opens "Change dates" (RP-28) — the only way to change the plan's window. */
  onChangeDates: () => void;
}

export function PlanSetupDialog({ plan, open, onOpenChange, onChangeDates }: Props) {
  const update = useUpdateResourcePlan(plan.id);

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: prefillValues(plan),
  });

  // The latest plan, reachable from the open-effect without making it a dependency. The
  // query hands back a NEW object identity on every background refetch, and a reset on that
  // identity would silently discard whatever the planner is part-way through typing.
  const latestPlan = useRef(plan);
  useEffect(() => {
    latestPlan.current = plan;
  }, [plan]);

  // Reset to this plan's current values every time the dialog OPENS — it may have been
  // opened once already for a plan that has since changed underneath it — and at no other
  // time, which is why the plan is read from the ref rather than from a dependency.
  const planId = plan.id;
  const resetForPlanId = useRef<string | null>(null);
  useEffect(() => {
    if (!open) {
      resetForPlanId.current = null;
      return;
    }
    if (resetForPlanId.current === planId) return;
    resetForPlanId.current = planId;
    form.reset(prefillValues(latestPlan.current));
  }, [open, planId, form]);

  const values = form.watch();
  const monthly = values.budgetCadence === "Monthly";
  const feeAllowed = !(values.pricingModel === "TimeAndMaterials" && monthly);

  const willConvertToMonthByMonth = plan.entryMode === "SteadyState" && !monthly;
  // Monthly runs on one hours-per-month figure, exactly as a plan created Monthly does —
  // unless a line's months differ, which one figure cannot hold without inventing data.
  const monthByMonthOnMonthly = plan.entryMode === "MonthByMonth" && monthly;
  const unevenLines = monthByMonthOnMonthly ? unevenRunRateLines(plan.lines, plan.months) : [];
  const willConvertToSteadyState = monthByMonthOnMonthly && unevenLines.length === 0;
  const staysMonthByMonth = monthByMonthOnMonthly && unevenLines.length > 0;

  // A Total plan is measured over a window with an end; one with no end month has to get
  // one through "Change dates" before its cadence can switch.
  const needsEndMonth = values.budgetCadence === "Total" && plan.endMonth === null;

  // The saved fee was entered for the saved cadence, so a cadence change needs a new one.
  const needsNewFee =
    plan.contractedFee !== null && values.budgetCadence !== plan.budgetCadence && feeAllowed;

  function onCadenceChange(next: FormValues["budgetCadence"]) {
    form.setValue("budgetCadence", next);
    if (plan.contractedFee === null) return;
    form.setValue("contractedFee", next === plan.budgetCadence ? plan.contractedFee : "");
    form.clearErrors("contractedFee");
  }

  function onSubmit(input: FormValues) {
    const blank = (v: string | undefined) => (v === undefined || v === "" ? null : v);
    const patch: Partial<CreatePlanInput> = {};

    if (input.name !== plan.name) patch.name = input.name;
    if (input.pricingModel !== plan.pricingModel) patch.pricingModel = input.pricingModel;
    if (input.budgetCadence !== plan.budgetCadence) patch.budgetCadence = input.budgetCadence;
    if (input.rateMode !== plan.rateMode) patch.rateMode = input.rateMode;
    if (needsEndMonth) return;
    if (needsNewFee && blank(input.contractedFee) === null) {
      form.setError("contractedFee", {
        message: `Enter the ${input.budgetCadence === "Monthly" ? "monthly" : "total"} fee — the old one was a ${plan.budgetCadence.toLowerCase()} figure`,
      });
      return;
    }

    // Fee and basis travel together — recompute both whenever either the fee, the Pricing
    // Model, or the Budget Cadence changed, so the basis never drifts from the cadence it is
    // meant to mirror (RP-1.2). A fee the plan cannot carry is nulled outright, along with
    // the reference that documents it — a SOW number for a fee that no longer exists is an
    // orphan.
    const typedFee = feeAllowed ? blank(input.contractedFee) : null;
    const feeChanged = !sameDecimal(typedFee, plan.contractedFee ?? null);
    const nextFee = feeChanged ? typedFee : (plan.contractedFee ?? null);
    const nextBasis = nextFee !== null ? input.budgetCadence : null;
    if (feeChanged || nextBasis !== (plan.contractedFeeBasis ?? null)) {
      patch.contractedFee = nextFee;
      patch.contractedFeeBasis = nextBasis;
    }

    const nextReference = feeAllowed ? blank(input.contractedFeeReference) : null;
    if (nextReference !== (plan.contractedFeeReference ?? null)) {
      patch.contractedFeeReference = nextReference;
    }

    const nextContingency = blank(input.contingencyPercent);
    if (!sameDecimal(nextContingency, plan.contingencyPercent ?? null)) {
      patch.contingencyPercent = nextContingency;
    }

    const nextNotes = blank(input.commercialNotes);
    if (nextNotes !== (plan.commercialNotes ?? null)) patch.commercialNotes = nextNotes;

    if (willConvertToMonthByMonth) patch.entryMode = "MonthByMonth";
    if (willConvertToSteadyState) patch.entryMode = "SteadyState";

    if (Object.keys(patch).length === 0) {
      onOpenChange(false);
      return;
    }

    update.mutate(patch, { onSuccess: () => onOpenChange(false) });
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Edit setup</DialogTitle>
          <DialogDescription>
            The data points this plan was created with. Only available while it is Open.
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Plan Name</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid gap-4 sm:grid-cols-3">
              <FormField
                control={form.control}
                name="pricingModel"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Pricing Model</FormLabel>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <FormControl>
                        <SelectTrigger className="w-full">
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="TimeAndMaterials">Time & Materials</SelectItem>
                        <SelectItem value="FixedPrice">Fixed Price</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="budgetCadence"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Budget Cadence</FormLabel>
                    <Select
                      value={field.value}
                      onValueChange={(v) => {
                        if (isBudgetCadence(v)) onCadenceChange(v);
                      }}
                    >
                      <FormControl>
                        <SelectTrigger className="w-full">
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="Total">Total</SelectItem>
                        <SelectItem value="Monthly">Monthly</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="rateMode"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Rate Mode</FormLabel>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <FormControl>
                        <SelectTrigger className="w-full">
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="Card">Rate Card</SelectItem>
                        <SelectItem value="ClientDictated">Client-Dictated</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {willConvertToMonthByMonth && (
              <p className="flex items-start gap-2 rounded-md bg-warning-100 p-2.5 text-sm text-warning-700 dark:text-warning-400">
                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                This plan runs on one hours-per-month figure per line (Steady-State), which only
                works on a Monthly cadence. Saving this will switch it to month-by-month entry,
                copying each line&rsquo;s figure into every month of the grid.
              </p>
            )}

            {willConvertToSteadyState && (
              <p className="rounded-md bg-info-100 p-2.5 text-sm text-info-700 dark:text-info-400">
                Saving will switch this plan to one hours-per-month figure per line, like any
                Monthly plan.
              </p>
            )}
            {staysMonthByMonth && (
              <p className="flex items-start gap-2 rounded-md bg-warning-100 p-2.5 text-sm text-warning-700 dark:text-warning-400">
                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                <span>
                  These lines have different hours in different months, so this plan stays month by
                  month: {unevenLines.join(", ")}. Give each line the same hours every month to
                  switch it to one figure per month.
                </span>
              </p>
            )}

            <div className="flex items-center justify-between gap-3 rounded-md border border-border px-3 py-2 text-sm">
              <span>
                <span className="text-muted-foreground">Dates </span>
                <span className="font-medium">
                  {formatPlanMonth(plan.startMonth)} &ndash;{" "}
                  {plan.endMonth === null ? "Planning Horizon" : formatPlanMonth(plan.endMonth)}
                </span>
              </span>
              <Button type="button" size="sm" variant="outline" onClick={onChangeDates}>
                Change dates
              </Button>
            </div>
            {needsEndMonth && (
              <p className="text-sm text-destructive">
                A Total-cadence plan needs an end month. Set one with Change dates first.
              </p>
            )}

            <FormField
              control={form.control}
              name="contractedFee"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{monthly ? "Monthly" : "Total"} Contracted Fee</FormLabel>
                  <FormControl>
                    <div className="relative">
                      <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
                        $
                      </span>
                      <Input
                        inputMode="decimal"
                        placeholder={feeAllowed ? "378387.36" : "not applicable"}
                        disabled={!feeAllowed}
                        {...field}
                        value={field.value ?? ""}
                        className="pl-6"
                      />
                    </div>
                  </FormControl>
                  <FormDescription>
                    {needsNewFee
                      ? `The saved fee (${plan.contractedFee}) was a ${plan.budgetCadence.toLowerCase()} figure. Enter the ${monthly ? "monthly" : "total"} fee for the new cadence.`
                      : feeAllowed
                        ? "What was presented to the client."
                        : "Monthly T&M is a run-rate — switch the cadence to Total to record a ceiling."}
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            {feeAllowed && (
              <FormField
                control={form.control}
                name="contractedFeeReference"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Source reference{" "}
                      <span className="font-normal text-muted-foreground">(optional)</span>
                    </FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Quote, SOW or CO number"
                        {...field}
                        value={field.value ?? ""}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            <p className="text-sm text-muted-foreground">
              Estimated hours are set on the Estimates tab.
            </p>

            <FormField
              control={form.control}
              name="contingencyPercent"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Contingency %{" "}
                    <span className="font-normal text-muted-foreground">(optional)</span>
                  </FormLabel>
                  <FormControl>
                    <Input inputMode="decimal" {...field} value={field.value ?? ""} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="commercialNotes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Notes</FormLabel>
                  <FormControl>
                    <Textarea rows={3} {...field} value={field.value ?? ""} />
                  </FormControl>
                  <FormDescription>
                    Expenses, credits and payment terms are recorded here as notes only. USD.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={update.isPending}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={update.isPending || needsEndMonth}>
                {update.isPending ? "Saving…" : "Save changes"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
